import { useEffect, useState } from 'react'
import { Button, Buttons, Content, Header, Modal, Title, Toggle, Toolbar, toastController } from '@/ui'
import { useAuthStore } from '@/stores/auth'
import { usePersonalizationStore } from '@/stores/personalization'
import {
  HUB_CARD_DEFINITIONS,
  buildLandingOptions,
  mergeHubPersonalization
} from '@/config/hubPersonalization'
import './PersonalizationChecklistModal.css'

export interface PersonalizationChecklistModalProps {
  isOpen?: boolean
  showUseDefaults?: boolean
  onDismiss?: () => void
  onSaved?: () => void
}

async function showToast (message: string, color = 'danger') {
  const toast = await toastController.create({ message, duration: 2200, color })
  await toast.present()
}

export function PersonalizationChecklistModal ({
  isOpen = false,
  showUseDefaults = true,
  onDismiss,
  onSaved
}: PersonalizationChecklistModalProps) {
  const hasLeagueAccess = useAuthStore((s) => s.hasLeagueAccess)
  const saveHubPersonalization = usePersonalizationStore((s) => s.saveHubPersonalization)

  const [saving, setSaving] = useState(false)
  const [draftVisibleCards, setDraftVisibleCards] = useState<Record<string, boolean>>({})
  const [draftDefaultLanding, setDraftDefaultLanding] = useState('hub')

  const landingOptions = buildLandingOptions({ hasLeagueAccess })

  useEffect(() => {
    if (!isOpen) return
    // resetDraft
    const merged = mergeHubPersonalization(useAuthStore.getState().userProfile?.hubPersonalization)
    setDraftVisibleCards({ ...merged.visibleCards })
    setDraftDefaultLanding(merged.defaultLanding)
  }, [isOpen])

  function setCardVisible (cardId: string, visible: boolean) {
    setDraftVisibleCards((prev) => ({
      ...prev,
      [cardId]: visible
    }))
  }

  async function savePreferences () {
    setSaving(true)
    try {
      const result = await saveHubPersonalization({
        visibleCards: draftVisibleCards,
        defaultLanding: draftDefaultLanding
      })
      if (!result.success) {
        await showToast(result.error || 'Could not save preferences')
        return
      }
      onSaved?.()
      onDismiss?.()
    } finally {
      setSaving(false)
    }
  }

  async function useDefaults () {
    setSaving(true)
    try {
      const result = await saveHubPersonalization({ useDefaults: true })
      if (!result.success) {
        await showToast(result.error || 'Could not save preferences')
        return
      }
      onSaved?.()
      onDismiss?.()
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onDidDismiss={() => onDismiss?.()} className="c-personalization-checklist-modal" ariaLabel="Customize your home">
      <Header>
        <Toolbar>
          <Title>Customize your home</Title>
          <Buttons slot="end">
            <Button fill="clear" onClick={() => onDismiss?.()}>Close</Button>
          </Buttons>
        </Toolbar>
      </Header>
      <Content padding>
        <div className="checklist-modal">
          <p className="checklist-lead">
            Choose what appears on your home screen and where the app opens.
            You can change this anytime in Profile → Settings.
          </p>

          <h3 className="checklist-heading">Home sections</h3>
          <div className="toggle-list">
            {HUB_CARD_DEFINITIONS.map((card) => (
              <div key={card.id} className="toggle-row">
                <div className="toggle-copy">
                  <span className="toggle-label">{card.label}</span>
                  <span className="toggle-desc">{card.description}</span>
                </div>
                <Toggle
                  checked={draftVisibleCards[card.id] !== false}
                  ariaLabel={card.label}
                  onChange={(checked) => setCardVisible(card.id, checked)}
                />
              </div>
            ))}
          </div>

          <h3 className="checklist-heading">When I open the app</h3>
          <div className="landing-list">
            {landingOptions.map((option) => (
              <label
                key={option.id}
                className={`landing-option${draftDefaultLanding === option.id ? ' selected' : ''}`}
              >
                <input
                  type="radio"
                  name="defaultLanding"
                  value={option.id}
                  checked={draftDefaultLanding === option.id}
                  onChange={() => setDraftDefaultLanding(option.id)}
                />
                <div className="landing-copy">
                  <span className="landing-label">{option.label}</span>
                  <span className="landing-desc">{option.description}</span>
                </div>
              </label>
            ))}
          </div>

          <div className="checklist-actions">
            <Button
              expand="block"
              color="primary"
              disabled={saving}
              onClick={savePreferences}
            >
              {saving ? 'Saving…' : 'Save preferences'}
            </Button>
            {showUseDefaults && (
              <Button
                expand="block"
                fill="outline"
                color="primary"
                disabled={saving}
                onClick={useDefaults}
              >
                Use defaults for now
              </Button>
            )}
          </div>
        </div>
      </Content>
    </Modal>
  )
}
